import { useForm } from 'react-hook-form';
import {
  Button,
  Container,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Typography,
  Grid,
  Box,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useState } from 'react';
import { dropdownFetch, request, toast } from '../../../../../../src/utils';
import { FormProvider, SelectTag } from '../../../../../components';

const initialRoomChargeModeDataObj = {
  data: [],
  loading: false
};

export const RoomChart = () => {
  const [roomChartDataObj, setRoomChartDataObj] = useState(initialRoomChargeModeDataObj);
  const [ipdData, setIpdData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ward, setWard] = useState([]);

  const methods = useForm();
  const selectedWard = methods.watch('ward');

  const fetchRoomData = async () => {
    setRoomChartDataObj({ loading: true });
    const response = await request('/room-master-list', { method: 'GET' });
    if (response.success) {
      setRoomChartDataObj({ loading: false, data: response.data });
    } else {
      toast('Failed to load Room Chart', { variant: 'error' });
      setRoomChartDataObj({ loading: false });
    }
  };

  const fetchIpdData = async () => {
    const response = await request('/indoor-registeration-list-admit-patient', { method: 'GET' });
    if (response.success) {
      setIpdData(response.data);
    } else {
      toast('Failed to load IPD data', { variant: 'error' });
    }
  };

  const getWardDropdown = useCallback(async () => {
    const data = await dropdownFetch('ward');
    setWard(data);
  }, []);

  useEffect(() => {
    fetchRoomData();
    fetchIpdData();
    getWardDropdown();
  }, [getWardDropdown]);

  const filteredRooms = Array.isArray(roomChartDataObj.data)
    ? roomChartDataObj.data.filter((room) => !selectedWard || room.ward?._id === selectedWard)
    : [];

  const getPatientDataForRoom = (roomId) => {
    return ipdData.filter((ipd) => ipd?.consultant_detail?.room?._id === roomId);
  };

  return (
    <Paper sx={{ p: 4, boxShadow: '0px 2px 10px #0099ff', backgroundColor: '#f5f5f5' }}>
      <Typography variant="h3" textAlign="center" color="primary" gutterBottom fontWeight={700}>
        Room Chart
      </Typography>
      <FormProvider methods={methods}>
        <Grid container spacing={1}>
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, marginBottom: '1%' }}>
            <Stack spacing={0.5} direction="row" alignItems="center">
              <Typography sx={{ flex: 0.1, fontWeight: 700, fontSize: '1.4rem' }}>Ward Name</Typography>
              <SelectTag formControlSX={{ flex: 1 }} name="ward" menus={ward} placeholder="Select Ward" />
            </Stack>
          </Grid>
        </Grid>
      </FormProvider>

      {loading || roomChartDataObj.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredRooms.map((room) => (
            <Grid item xs={12} key={room._id}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: 'white',
                  boxShadow: 10
                }}
              >
                <Grid container spacing={2}>
                  {room.beds.map((bed, idx) => {
                    const patientDataList = getPatientDataForRoom(room._id);
                    const patientData =
                      patientDataList.find((ipd) => ipd.consultant_detail?.bed_no === bed.bed_no) || null;

                    return (
                      <Grid item xs={12} sm={3} md={3} key={idx}>
                        <Box
                          sx={{
                            height: 190,
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            backgroundColor: bed.occupy_status === 'B' ? 'lightblue' : 'white',
                            color: 'black',
                            borderRadius: 2,
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            boxShadow: 3
                          }}
                        >
                          {bed.occupy_status === 'B' && patientData ? (
                            <>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                IPD: {patientData.ipd_no || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                UHID: {patientData.uhid || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                Patient: {patientData.patient.name || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                Mobile: {patientData.care_taker.mobile_1 || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                Doctor: {patientData.consultant_detail.doctor.name || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                Floor: {room.floor?.name || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                Ward: {room.ward.name || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                Room: {room.room_no || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                Bed No.: {bed.bed_no || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                Status: {bed.occupy_status === 'B' ? 'Occupied' : 'Free'}
                              </Typography>
                            </>
                          ) : (
                            <>
                              {/* Display details when bed is not occupied */}
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body1">
                                Floor: {room.floor?.name || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body1">
                                Ward: {room.ward.name || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body1">
                                Room: {room.room_no || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body1">
                                Bed No: {bed.bed_no}
                              </Typography>
                              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }} variant="body2">
                                Status: {bed.occupy_status === 'B' ? 'Occupied' : 'Free'}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
};
